import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useSnackbarStore } from '@/stores/snackbar';
import productUtils from '@/utils/product-utils';
import { useProductIndicators } from '@/composables/product-indicators';
import { size as _size, find, get, map } from 'lodash';
import { mapActions, mapState } from 'pinia';

const mixin = {
  // TODO: vuetify, this just make compile working, need to convert to composable and wire up
  data: () => ({ hasProductsExistingFromNewInTemplateEnabled: true }),
  computed: {
    ...mapState(useContextStore, ['clientConfig']),
    ...mapState(useAssortmentCanvasStore, [
      'spacebreaks',
      'spacebreaksOrderedObject',
      'isSpacebreakLocked',
      'getIndexedCanvasProducts',
    ]),

    spacebreaksIndexedByIdByCluster() {
      return this.getSpacebreaksIndexedById[this.selectedCanvas.clusterId];
    },

    getProductTag() {
      const { getProductModellingTag: getProductTagForProduct } = useProductIndicators();
      const isUnclustered = !this.selectedCanvas.clusterId;
      return getProductTagForProduct(this.product, isUnclustered);
    },

    spacebreaksNames() {
      const names = map(this.spacebreaks, ({ _id, name, shortName, size, fillOverride }) => ({
        value: _id,
        title: `${shortName} - ${name}`,
        disabled: this.isSpacebreakLocked(_id),
        size,
        fillOverride,
      }));
      return [
        ...names,
        {
          value: null,
          title: this.$t('assortmentCanvasPage.tileViewPage.notListed'),
          disabled: false,
          size: 0,
          fillOverride: 0,
        },
      ];
    },

    spacebreaksShortNames() {
      const shortNames = map(this.spacebreaks, ({ _id, shortName, size, fillOverride }) => ({
        value: _id,
        title: shortName,
        disabled: this.isSpacebreakLocked(_id),
        size,
        fillOverride,
      }));
      return [
        ...shortNames,
        {
          value: null,
          title: this.$t('assortmentCanvasPage.tileViewPage.notListed'),
          disabled: false,
          size: 0,
          fillOverride: 0,
        },
      ];
    },

    spacebreaksShortNamesNone() {
      const shortNames = map(this.spacebreaks, ({ _id, shortName, size, fillOverride }) => ({
        value: _id,
        title: shortName,
        disabled: this.isSpacebreakLocked(_id),
        size,
        fillOverride,
      }));
      return [
        ...shortNames,
        {
          value: null,
          title: this.$t('general.none'),
          disabled: false,
          size: 0,
          fillOverride: 0,
        },
      ];
    },

    selectedSpacebreak() {
      return productUtils.selectedSpacebreak(
        this.spacebreaksNames,
        this.product.currentSpacebreakId
      );
    },

    selectedSpacebreakName() {
      const spacebreak = find(this.spacebreaks, { _id: this.product.currentSpacebreakId });
      if (spacebreak) {
        return spacebreak.name;
      }
      // currentSpacebreakId is null
      if (!this.product.assortment || this.product.isLocal) {
        return this.$t('assortmentCanvas.notForAssortment');
      }
      return !this.product.isEligible
        ? this.$t('assortmentCanvas.notInStoreClass')
        : this.$t('assortmentCanvasPage.tileViewPage.notListed');
    },

    canProductBeMoved() {
      return this.product.assortment && !this.product.isLocal && this.product.isEligible;
    },
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'updateAssortmentCanvasProductById',
      'setShowDashboard',
      'setDashboardProduct',
      'setShowDashboard',
      'updateCanvasProduct',
    ]),
    ...mapActions(useSnackbarStore, ['showError']),
    ...mapActions(useScenarioProductsStore, ['getAssociatedPallets']),

    buildProductPositioningInfo({ spacebreakId, spacebreaksIndexedById, selectedPod }) {
      const spacebreakIdInfo = get(spacebreaksIndexedById, spacebreakId, {
        shortName: null,
        storeKeys: [],
      });
      const spacebreakIdPod = get(selectedPod, spacebreakId, 0);
      const noOfStores = _size(spacebreakIdInfo.storeKeys);
      return this.$t('assortmentCanvas.productPositioning', [
        get(spacebreakIdInfo, 'shortName', ''),
        noOfStores,
        noOfStores ? spacebreakIdPod : 0,
      ]);
    },

    extractProductPositioningInfo(product, clusterId, spacebreaksIndexedById, selectedPod) {
      // made this function a method rather than a computed so we can reuse it between list view and tile view
      const currentSpacebreakId = this.buildProductPositioningInfo({
        spacebreakId: product.currentSpacebreakId,
        spacebreaksIndexedById: spacebreaksIndexedById[clusterId],
        selectedPod,
      });
      const optimisedSpacebreakId = this.buildProductPositioningInfo({
        spacebreakId: product.optimisedSpacebreakId,
        spacebreaksIndexedById,
        selectedPod,
      });
      const originalSpacebreakId = this.buildProductPositioningInfo({
        spacebreakId: product.originalSpacebreakId,
        spacebreaksIndexedById,
        selectedPod,
      });
      return {
        currentSpacebreakId,
        optimisedSpacebreakId,
        originalSpacebreakId,
      };
    },

    podAndStoreCounts(product, clusterId, spacebreaksIndexedByIdByCluster) {
      return {
        currentSpacebreak: {
          id: product.currentSpacebreakId,
          shortName: get(
            this.getSpacebreakInfo(
              product.currentSpacebreakId,
              clusterId,
              spacebreaksIndexedByIdByCluster
            ),
            'shortName',
            ''
          ),
          pod: this.selectedPod[product.currentSpacebreakId],
          storeCount: this.numberOfStores(
            product.currentSpacebreakId,
            spacebreaksIndexedByIdByCluster
          ),
        },
        originalSpacebreak: {
          id: product.originalSpacebreakId,
          shortName: get(
            this.getSpacebreakInfo(
              product.originalSpacebreakId,
              clusterId,
              spacebreaksIndexedByIdByCluster
            ),
            'shortName',
            ''
          ),
          pod: this.selectedPod[product.originalSpacebreakId],
          storeCount: this.numberOfStores(
            product.originalSpacebreakId,
            spacebreaksIndexedByIdByCluster
          ),
        },
        optimisedSpacebreak: {
          id: product.optimisedSpacebreakId,
          shortName: get(
            this.getSpacebreakInfo(
              product.optimisedSpacebreakId,
              clusterId,
              spacebreaksIndexedByIdByCluster
            ),
            'shortName',
            ''
          ),
          pod: this.selectedPod[product.optimisedSpacebreakId],
          storeCount: this.numberOfStores(
            product.optimisedSpacebreakId,
            spacebreaksIndexedByIdByCluster
          ),
        },
      };
    },

    getSpacebreakInfo(spacebreakId, clusterId, spacebreaksIndexedByIdByCluster) {
      return get(spacebreaksIndexedByIdByCluster, spacebreakId, {
        shortName: null,
        storeKeys: [],
      });
    },

    numberOfStores(spacebreakId, spacebreaksIndexedByIdByCluster) {
      const spacebreakIdInfo = get(spacebreaksIndexedByIdByCluster, spacebreakId, {
        storeKeys: [],
      });
      return spacebreakIdInfo.storeKeys.length;
    },

    async changeSpacebreak(spacebreakId) {
      this.setShowDashboard(false);
      await productUtils.moveProductToNewSpacebreak(
        this.product,
        spacebreakId || null,
        {
          updateAssortmentCanvasProductById: this.updateAssortmentCanvasProductById,
          updateCanvasProduct: this.updateCanvasProduct,
          showError: this.showError,
          spacebreaksOrderedObject: this.spacebreaksOrderedObject,
          getAssociatedPallets: this.getAssociatedPallets,
          getIndexedCanvasProducts: this.getIndexedCanvasProducts,
        },
        this.$t('assortmentCanvas.invalidPallets')
      );
    },

    openProductDashboard() {
      this.show = false;
      this.setDashboardProduct(this.product);
      this.setShowDashboard(true);
    },
  },
};

export default mixin;
